import { FC, useEffect, useMemo, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, MenuItem, Skeleton, Stack, TextField } from '@mui/material'

import { Analyst } from '../../../../../types/Analyst'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalImageUpload } from '../../../../components/InternalImageUpload/InternalImageUpload'
import { AnalystFormValues, getIntercomAdminOptions, toAnalystFormValues, toAnalystMutation } from '../../helpers/analystHelpers'
import { AnalystMutation, IntercomAdmin } from '../../types/AnalystMutation'

const portraitCrop = { aspect: 1, shape: 'round' as const, resize: { width: 150, height: 150 } }
// MUI Select needs a string value; '' stands for "Disabled" (null).
const disabledIntercomValue = ''
const newAnalystKey = 'new'

type AnalystEditDialogProps = {
  open: boolean
  analyst?: Analyst
  intercomAdmins?: IntercomAdmin[]
  isLoadingIntercomAdmins?: boolean
  isSaving?: boolean
  onClose: () => void
  onSave: (analyst: AnalystMutation) => void
}

export const AnalystEditDialog: FC<AnalystEditDialogProps> = ({ open, analyst, intercomAdmins, isLoadingIntercomAdmins, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<AnalystFormValues>({ mode: 'onChange', defaultValues: toAnalystFormValues(analyst) })
  const analystRef = useRef(analyst)
  analystRef.current = analyst
  const formKey = open ? analyst?.id ?? newAnalystKey : undefined

  // Only when the dialog opens or another analyst is shown, so a parent's refetch doesn't wipe the edits.
  useEffect(() => {
    if (formKey !== undefined) reset(toAnalystFormValues(analystRef.current))
  }, [formKey, reset])

  const storedIntercomId = analyst?.intercomId ?? null
  const intercomOptions = useMemo(
    () => (intercomAdmins ? getIntercomAdminOptions(intercomAdmins, storedIntercomId, t('internal-analysts:analysts_intercom_disabled')) : undefined),
    [intercomAdmins, storedIntercomId, t]
  )

  const submit = handleSubmit((values) => onSave(toAnalystMutation(analyst, values)))

  return (
    <InternalFormDialog
      open={open}
      title={analyst ? t('internal-analysts:analysts_edit') : t('internal-analysts:analysts_create')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="picture"
          control={control}
          render={({ field }) => (
            <InternalImageUpload
              value={field.value}
              onChange={field.onChange}
              type="portrait"
              label={t('internal-analysts:analysts_picture')}
              crop={portraitCrop}
              removable
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ validate: (value) => !!value.trim() }}
          render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:analysts_name')} required />}
        />
        <Controller
          name="title"
          control={control}
          render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:analysts_title')} />}
        />
        <Controller
          name="intercomId"
          control={control}
          render={({ field }) =>
            isLoadingIntercomAdmins ? (
              <Skeleton variant="rectangular" height={40} />
            ) : intercomOptions ? (
              <TextField
                select
                size="small"
                label={t('internal-analysts:analysts_intercom_admin')}
                helperText={t('internal-analysts:analysts_intercom_admin_help')}
                value={field.value ?? disabledIntercomValue}
                onChange={(event) => field.onChange(event.target.value === disabledIntercomValue ? null : event.target.value)}
              >
                {intercomOptions.map((option) => (
                  <MenuItem key={option.id ?? disabledIntercomValue} value={option.id ?? disabledIntercomValue}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                size="small"
                disabled
                label={t('internal-analysts:analysts_intercom_admin')}
                value={field.value ?? t('internal-analysts:analysts_intercom_disabled')}
                helperText={t('internal-analysts:analysts_intercom_unavailable')}
              />
            )
          }
        />
        <Controller
          name="inactive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('internal-analysts:analysts_inactive')}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
